import { computed, type Ref } from 'vue';

import { notificationApi } from '@/domains/notifications/api';
import { priorityToType, type NoticeType } from '@/domains/notifications/notice-icons';
import { STORAGE_KEYS } from '@/platform/auth-runtime/token-storage';
import { useUserStore } from '@/platform/identity/store';
import { DocsLinks } from '@/shared/docs';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';

import type { UnreadListResponse } from '@/domains/notifications/api';

// Bare key prefixes. The actual queries are stored under a per-account
// suffix below (`unreadKeyFor` / `listKeyFor`) — invalidateQueries with
// the prefix still matches because vue-query does prefix-key matching, so
// cache invalidation paths keep working unchanged.
const UNREAD_KEY = ['notification', 'unread-list'] as const;
export const NOTIFICATION_LIST_KEY = ['notification', 'list'] as const;

function unreadKeyFor(accountID: string | null | undefined): QueryKey {
  return [...UNREAD_KEY, accountID ?? null];
}

function listKeyFor(accountID: string | null | undefined): QueryKey {
  return [...NOTIFICATION_LIST_KEY, accountID ?? null];
}

// Invalidates both host vue-query keys (unread popover/banner + paginated
// list page). Used by WS push, mark-* mutations, and the sub:invalidate
// handler in sub-broadcaster.ts.
export function invalidateNotifications(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: UNREAD_KEY });
  void qc.invalidateQueries({ queryKey: NOTIFICATION_LIST_KEY });
}

// Backend wire format: msg_type is a numeric MsgType enum (9 = BROADCAST,
// others map to priority buckets). The 9-flag is captured into is_broadcast
// before msg_type gets overwritten with the priority-derived string
// ('error' | 'info' | 'success' | 'warning') so banner + dropdown can both
// treat msg_type as a string downstream.
const BROADCAST_MSG_TYPE = 9;

// NotifyType wire encoding (mirrors proto Api.Notification.NotifyType but
// the cloud-typings namespace is declaration-only, so we redeclare for
// runtime use). 0 = inbox/notification bucket, 1 = top-bar banner, 2 = email.
export const NotifyType = {
  NOTIFICATION: 0,
  BAR: 1,
  EMAIL: 2,
} as const;
export type NotifyType = (typeof NotifyType)[keyof typeof NotifyType];

// Wire encoding for NotifyMsg.state and the toggle-read-state `state` field.
// No proto enum for this 2-value binary, so name it here once.
export const NotifyState = {
  UNREAD: 0,
  READ: 1,
} as const;
export type NotifyState = (typeof NotifyState)[keyof typeof NotifyState];

export type NotifyJump = {
  link: string;
  linkType?: 'DOCS';
  open_in_new_tab?: boolean;
};

export type FormattedNotifyMsg = Omit<Api.Notification.NotifyMsg, 'msg_type'> & {
  msg_type: NoticeType;
  is_broadcast: boolean;
  jumpDetail?: NotifyJump;
};

export type FormattedUnreadList = {
  barNotifyList?: Omit<Api.Notification.ListNotifyMsgRsp, 'notify_msgs'> & {
    notify_msgs?: FormattedNotifyMsg[];
  };
  notifyList?: Omit<Api.Notification.ListNotifyMsgRsp, 'notify_msgs'> & {
    notify_msgs?: FormattedNotifyMsg[];
  };
};

export type FormattedNotificationList = {
  notify_msgs: FormattedNotifyMsg[];
  total: number;
};

function resolveJump(msg: Api.Notification.NotifyMsg): NotifyJump | undefined {
  const code = msg.param?.link_address_code;
  if (!code || !msg.param) return undefined;
  let link = '';
  let linkType: 'DOCS' | undefined;
  switch (code) {
    case 'CLUSTER_EXCEPTIONS':
    case 'STORAGE_REACH_CAPACITY_LIMIT':
    case 'CLUSTER_RESUME_FAILED':
    case 'CLUSTER_SUSPEND_FAILED':
    case 'CLUSTER_NLB_CREATE_FAILED':
    case 'CLUSTER_SCALE_IN_FAILED':
    case 'CLUSTER_SCALE_OUT_FAILED':
    case 'CLUSTER_SCALE_UP_FAILED':
    case 'CLUSTER_SCALE_DOWN_FAILED':
    case 'CLUSTER_EXPAND_VOLUME_FAILED':
    case 'CLUSTER_OPERATION_SUCCEEDED':
      link = msg.param.cluster_id ? `/clusters/${msg.param.cluster_id}` : '';
      break;
    case 'USER_ROLE_GRANTED_NOTIFICATION':
    case 'USER_ROLE_REVOKED_NOTIFICATION': {
      const ubid = localStorage.getItem(STORAGE_KEYS.ubid);
      link = ubid ? `/management/member/${ubid}?tab=privileges` : '';
      break;
    }
    case 'FREE_TIER_AVAILABLE':
      link = DocsLinks.FreeTier;
      linkType = 'DOCS';
      break;
  }
  if (!link) return undefined;
  return { link, linkType, open_in_new_tab: msg.param.open_in_new_tab };
}

function formatBucket(
  bucket: Api.Notification.ListNotifyMsgRsp | undefined,
): FormattedUnreadList['barNotifyList'] {
  if (!bucket) return undefined;
  return {
    ...bucket,
    notify_msgs: (bucket.notify_msgs ?? []).map((msg) => {
      // Capture broadcast flag BEFORE overwriting msg_type. Destructure-
      // rename drops the wire's numeric msg_type, then we re-emit msg_type
      // as the priority-derived NoticeType string for AppBanner / dropdown
      // to read uniformly.
      const { msg_type: rawMsgType, ...rest } = msg;
      const is_broadcast = rawMsgType === BROADCAST_MSG_TYPE;
      const msg_type = priorityToType(msg.msg_priority);
      return { ...rest, msg_type, is_broadcast, jumpDetail: resolveJump(msg) };
    }),
  };
}

// Paginated full inbox (the /notification page table). queryKey includes
// the page slice so switching page or page size lands in a separate cache
// entry; the invalidate path refetches whichever slice is currently
// active. Independent from UNREAD_KEY — the popover + flashbar reads
// unread only, the page reads all states.
export function useNotificationList(params: Ref<{ offset: number; limit: number }>) {
  const { info } = storeToRefs(useUserStore());
  return useQuery<FormattedNotificationList>({
    queryKey: computed(() => [
      ...listKeyFor(info.value?.accountID),
      params.value.offset,
      params.value.limit,
    ]),
    queryFn: async () => {
      const res = await notificationApi['/notification/list']({
        offset: params.value.offset,
        limit: params.value.limit,
        notify_type: NotifyType.NOTIFICATION,
      });
      const formatted = formatBucket(res);
      return {
        notify_msgs: formatted?.notify_msgs ?? [],
        total: res.total ?? 0,
      };
    },
    // Background poll while the page is mounted. WS invalidation is the
    // primary freshness path; this is the fallback for a silently dropped
    // socket. The query only exists on the /notification route, so the poll
    // is naturally scoped to when the table is on screen.
    refetchInterval: 60_000,
  });
}

export const useUnreadNotifications = () => {
  const { info } = storeToRefs(useUserStore());
  return useQuery<FormattedUnreadList>({
    queryKey: computed(() => unreadKeyFor(info.value?.accountID)),
    queryFn: async () => {
      const res: UnreadListResponse = await notificationApi['/notification/unread-list']({
        limit: 30,
        offset: 0,
      });
      return {
        barNotifyList: formatBucket(res.barNotifyList),
        notifyList: formatBucket(res.notifyList),
      };
    },
    // WS push (`ws-bridge.ts`) is the primary freshness path and invalidates
    // this key immediately. The 60s interval is a fallback in case the
    // socket drops.
    refetchInterval: 60_000,
  });
};

// Walk every cached /notification/list page slice and run `mapper` over its
// rows. Returns the pre-mutation snapshots paired with their queryKey so
// onError can put each slice back. Multiple slices coexist because
// `placeholderData: keepPreviousData` retains the previous page while a
// new one fetches.
type ListSliceSnapshot = Array<[QueryKey, FormattedNotificationList | undefined]>;

function applyOptimisticToListSlices(
  qc: QueryClient,
  accountID: string | null | undefined,
  mapper: (msg: FormattedNotifyMsg) => FormattedNotifyMsg,
): ListSliceSnapshot {
  const snaps: ListSliceSnapshot = [];
  const slices = qc.getQueriesData<FormattedNotificationList>({
    queryKey: listKeyFor(accountID),
  });
  for (const [key, data] of slices) {
    snaps.push([key, data]);
    if (!data) continue;
    qc.setQueryData<FormattedNotificationList>(key, {
      ...data,
      notify_msgs: data.notify_msgs.map(mapper),
    });
  }
  return snaps;
}

function rollbackListSlices(qc: QueryClient, snaps: ListSliceSnapshot): void {
  for (const [key, data] of snaps) {
    qc.setQueryData(key, data);
  }
}

// Optimistic mark-read: drop the row from the unread buckets (popover bell
// empties instantly) AND flip its `state` to READ in every cached list
// page slice (inbox row updates without waiting for refetch). Rollback
// both on error. After settle, invalidate to reconcile with the server.
export const useMarkRead = () => {
  const qc = useQueryClient();
  const { info } = storeToRefs(useUserStore());
  return useMutation({
    mutationFn: (item: FormattedNotifyMsg) =>
      notificationApi['/notification/toggle-read-state']({
        detail_biz_id: item.detail_biz_id,
        state: NotifyState.READ,
      }),
    onMutate: async (item: FormattedNotifyMsg) => {
      const detailBizId = item.detail_biz_id;
      const aid = info.value?.accountID;
      const unreadKey = unreadKeyFor(aid);
      await qc.cancelQueries({ queryKey: unreadKey });
      await qc.cancelQueries({ queryKey: listKeyFor(aid) });
      const unread = qc.getQueryData<FormattedUnreadList>(unreadKey);
      if (unread) {
        qc.setQueryData<FormattedUnreadList>(unreadKey, {
          // A notice delivered to both surfaces shares biz_id but each
          // delivery carries its own detail_biz_id. Marking the inbox copy
          // read (notify_type falsy) drops its banner twin by biz_id;
          // marking the banner copy itself drops it by detail_biz_id.
          barNotifyList: unread.barNotifyList && {
            ...unread.barNotifyList,
            notify_msgs: unread.barNotifyList.notify_msgs?.filter(
              item.notify_type
                ? (m) => m.detail_biz_id !== detailBizId
                : (m) => m.biz_id !== item.biz_id,
            ),
          },
          notifyList: unread.notifyList && {
            ...unread.notifyList,
            notify_msgs: unread.notifyList.notify_msgs?.filter(
              (m) => m.detail_biz_id !== detailBizId,
            ),
          },
        });
      }
      const lists = applyOptimisticToListSlices(qc, aid, (m) =>
        m.detail_biz_id === detailBizId ? { ...m, state: NotifyState.READ } : m,
      );
      return { unread, lists, unreadKey };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.unread && ctx.unreadKey) qc.setQueryData(ctx.unreadKey, ctx.unread);
      if (ctx?.lists) rollbackListSlices(qc, ctx.lists);
    },
    onSettled: () => invalidateNotifications(qc),
  });
};

// Optimistic mark-unread: flip the row's `state` to UNREAD in every cached
// list slice so the inbox cell updates without waiting for the request.
// UNREAD_KEY (popover) is deliberately not patched — re-adding to the
// unread bucket needs the full row, but the queries layer only has
// detail_biz_id. The popover catches up after the onSettled invalidate
// refetch; the brief count lag is acceptable since the user is acting on
// the list page, not watching the bell.
export const useMarkUnRead = () => {
  const qc = useQueryClient();
  const { info } = storeToRefs(useUserStore());
  return useMutation({
    mutationFn: (item: FormattedNotifyMsg) =>
      notificationApi['/notification/toggle-read-state']({
        detail_biz_id: item.detail_biz_id,
        state: NotifyState.UNREAD,
      }),
    onMutate: async (item: FormattedNotifyMsg) => {
      const detailBizId = item.detail_biz_id;
      const aid = info.value?.accountID;
      await qc.cancelQueries({ queryKey: listKeyFor(aid) });
      const lists = applyOptimisticToListSlices(qc, aid, (m) =>
        m.detail_biz_id === detailBizId ? { ...m, state: NotifyState.UNREAD } : m,
      );
      return { lists };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.lists) rollbackListSlices(qc, ctx.lists);
    },
    onSettled: () => invalidateNotifications(qc),
  });
};

// Optimistic mark-all-read: clear both unread buckets AND flip every row
// to READ in every cached list slice. Same rollback shape as useMarkRead.
export const useMarkAllRead = () => {
  const qc = useQueryClient();
  const { info } = storeToRefs(useUserStore());
  return useMutation({
    mutationFn: () => notificationApi['/notification/mark-all-read']({}),
    onMutate: async () => {
      const aid = info.value?.accountID;
      const unreadKey = unreadKeyFor(aid);
      await qc.cancelQueries({ queryKey: unreadKey });
      await qc.cancelQueries({ queryKey: listKeyFor(aid) });
      const unread = qc.getQueryData<FormattedUnreadList>(unreadKey);
      if (unread) {
        qc.setQueryData<FormattedUnreadList>(unreadKey, {
          barNotifyList: unread.barNotifyList && {
            ...unread.barNotifyList,
            notify_msgs: [],
          },
          notifyList: unread.notifyList && {
            ...unread.notifyList,
            notify_msgs: [],
          },
        });
      }
      const lists = applyOptimisticToListSlices(qc, aid, (m) => ({
        ...m,
        state: NotifyState.READ,
      }));
      return { unread, lists, unreadKey };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.unread && ctx.unreadKey) qc.setQueryData(ctx.unreadKey, ctx.unread);
      if (ctx?.lists) rollbackListSlices(qc, ctx.lists);
    },
    onSettled: () => invalidateNotifications(qc),
  });
};
